// src/pages/customer/CustomerForm.js
// Componente de formulario para crear/editar clientes (reutilizable en modal)

import React, { useState } from 'react';
import { Form, Row, Col, Card, Button, Spinner } from 'react-bootstrap';

const CustomerForm = ({ onSubmit, loading = false, initialData = null, submitText = "Registrar Cliente" }) => {
  // Normalizar datos de persona (pueden venir en raíz o en person)
  const p = initialData?.person;
  const email = initialData?.email ?? p?.email ?? '';
  const phone_whatsapp = initialData?.phone_whatsapp ?? p?.phone_whatsapp ?? '';
  const phone_2 = initialData?.phone_2 ?? '';
  const timezone = initialData?.timezone ?? 'America/Bogota';
  const marketing_opt_in = initialData?.marketing_opt_in ?? 0;
  const name = initialData?.name ?? p?.name ?? '';
  const surname = initialData?.surname ?? p?.surname ?? '';
  const lastname = initialData?.lastname ?? p?.lastname ?? '';
  const second_surname = initialData?.second_surname ?? p?.second_surname ?? '';
  const document = initialData?.document ?? p?.document ?? '';
  const id_document_type = initialData?.id_document_type ?? p?.id_document_type ?? 1;
  const id_person_type = initialData?.id_person_type ?? p?.id_person_type ?? 1;

  // Estados del formulario
  const [formData, setFormData] = useState({
    email,
    phone_whatsapp,
    phone_2,
    timezone,
    marketing_opt_in,
    name,
    surname,
    lastname,
    second_surname,
    document,
    id_document_type,
    id_person_type
  });

  // Opciones para los selects
  const documentTypes = [
    { id: 1, name: 'Cédula de Ciudadanía' },
    { id: 2, name: 'Cédula de Extranjería' },
    { id: 3, name: 'Pasaporte' },
    { id: 4, name: 'Tarjeta de Identidad' }
  ];

  const personTypes = [
    { id: 1, name: 'Persona Natural' },
    { id: 2, name: 'Persona Jurídica' }
  ];

  const timezones = [
    { value: 'America/Bogota', label: 'Colombia (Bogotá)' },
    { value: 'America/Mexico_City', label: 'México' },
    { value: 'America/New_York', label: 'Estados Unidos (Este)' },
    { value: 'America/Los_Angeles', label: 'Estados Unidos (Oeste)' }
  ];

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // Validar formulario
  const validateForm = () => {
    if (!formData.email) return 'El email es obligatorio';
    if (!formData.phone_whatsapp) return 'El teléfono WhatsApp es obligatorio';
    if (!formData.name) return 'El nombre es obligatorio';
    if (!formData.surname) return 'El apellido es obligatorio';
    if (!formData.document) return 'El documento es obligatorio';
    
    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return 'Formato de email inválido';
    
    // Validar formato de teléfono (solo números)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone_whatsapp)) return 'El teléfono debe tener 10 dígitos';
    
    return null;
  };

  // Construir payload para el backend
  const buildPayload = () => {
    // Para edición, enviar todos los campos de persona (incluidos apellidos) igual que en creación
    if (initialData) {
      const payload = {
        email: formData.email,
        phone_whatsapp: formData.phone_whatsapp,
        timezone: formData.timezone,
        marketing_opt_in: formData.marketing_opt_in,
        person: {
          name: formData.name,
          surname: formData.surname,
          lastname: formData.lastname,
          second_surname: formData.second_surname,
          document: formData.document,
          id_document_type: parseInt(formData.id_document_type),
          id_person_type: parseInt(formData.id_person_type)
        }
      };
      
      // Agregar phone_2 si tiene valor
      if (formData.phone_2) {
        payload.phone_2 = formData.phone_2;
      }
      
      return payload;
    }
    
    // Para creación, enviar todos los campos
    const payload = {
      email: formData.email,
      phone_whatsapp: formData.phone_whatsapp,
      timezone: formData.timezone,
      marketing_opt_in: formData.marketing_opt_in,
      person: {
        name: formData.name,
        surname: formData.surname,
        lastname: formData.lastname,
        second_surname: formData.second_surname,
        document: formData.document,
        id_document_type: parseInt(formData.id_document_type),
        id_person_type: parseInt(formData.id_person_type)
      }
    };
    
    // Agregar phone_2 si tiene valor
    if (formData.phone_2) {
      payload.phone_2 = formData.phone_2;
    }
    
    return payload;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (validation) {
      alert(validation); // En un caso real, usarías un sistema de notificaciones
      return;
    }

    const payload = buildPayload();
    await onSubmit(payload);
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Información del Cliente */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0">Información del Cliente</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ejemplo@correo.com"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Teléfono WhatsApp *</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone_whatsapp"
                  value={formData.phone_whatsapp}
                  onChange={handleChange}
                  placeholder="3001234567"
                  maxLength="10"
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Teléfono 2 (Opcional)</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone_2"
                  value={formData.phone_2}
                  onChange={handleChange}
                  placeholder="3001234567"
                  maxLength="10"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Zona Horaria</Form.Label>
                <Form.Select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="marketing_opt_in"
                  label="Acepta recibir comunicaciones de marketing"
                  checked={formData.marketing_opt_in === 1}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Información de la Persona */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0">Información Personal</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Juan"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Primer Apellido *</Form.Label>
                <Form.Control
                  type="text"
                  name="surname"
                  value={formData.surname}
                  onChange={handleChange}
                  placeholder="Pérez"
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Segundo Apellido</Form.Label>
                <Form.Control
                  type="text"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleChange}
                  placeholder="Gómez"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Apellido Adicional</Form.Label>
                <Form.Control
                  type="text"
                  name="second_surname"
                  value={formData.second_surname}
                  onChange={handleChange}
                  placeholder="López"
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Número de Documento *</Form.Label>
                <Form.Control
                  type="text"
                  name="document"
                  value={formData.document}
                  onChange={handleChange}
                  placeholder="123456789"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Documento</Form.Label>
                <Form.Select
                  name="id_document_type"
                  value={formData.id_document_type}
                  onChange={handleChange}
                >
                  {documentTypes.map(docType => (
                    <option key={docType.id} value={docType.id}>
                      {docType.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Persona</Form.Label>
                <Form.Select
                  name="id_person_type"
                  value={formData.id_person_type}
                  onChange={handleChange}
                >
                  {personTypes.map(personType => (
                    <option key={personType.id} value={personType.id}>
                      {personType.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-grid">
        <Button
          variant="primary"
          type="submit"
          disabled={loading}
          size="lg"
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Procesando...
            </>
          ) : (
            submitText
          )}
        </Button>
      </div>
    </Form>
  );
};

export default CustomerForm;
